/**
 * EWBCI API Client のエラー処理
 */

import { EWBCIClient } from "../core/client";

/**
 * EWBCI API エラーの種類
 */
export const EWBCIErrorType = {
    Unauthorized: 'Unauthorized', // ログインしていないか、現在ログイン中のセッション情報に異常です

    ServerConnectionError: 'ServerConnectionError', // LantanaAPI接続エラー (ネットワークエラーなど)
    EwbConnectionError: 'EwbConnectionError', // eWB接続エラー(タイムアウトなど)

    EwbciError: 'EwbciError', // eWBからエラーレスポンスが返ってきた
    ServerError: 'ServerError', // Lantanaからエラーレスポンスが返ってきた
    MessageError: 'MessageError', // メッセージ処理エラー
    ChunkError: 'ChunkError', // チャンクメッセージの処理でのエラー
    AssertError: 'AssertError', // アサーションエラー （内部エラー）

    Unknown: 'Unknown', // 不明なエラー
} as const;
export type EWBCIErrorType = typeof EWBCIErrorType[keyof typeof EWBCIErrorType];

/**
 * EWBCI API エラークラス
 * 
 * EWBCI API を呼び出す関数群で発生するエラーです。
 * - 本クライアントの関数はこのエラーをthrowすることがあるため、呼び出し元ではtry-catchで捕捉する必要があります。
 * - 本クライアントの関数を実装する際は、Errorをそのままthrowせず、必ずこのクラスでラップするようにしてください。
 */
export class EWBCIError extends Error {
    public readonly errorType: EWBCIErrorType;
    public readonly originalError?: Error;

    constructor(message: string, errorType: EWBCIErrorType, originalError?: unknown) {
        super(message);
        this.name = 'EWBCIError';
        this.errorType = errorType;
        if (originalError !== undefined) {
            if (originalError instanceof EWBCIError) {
                this.errorType = originalError.errorType; // 元のエラータイプを引き継ぐ
                this.originalError = originalError;
            } else if (originalError instanceof Error) {
                this.originalError = originalError;
            } else if (typeof originalError === 'string') {
                this.originalError = new Error(originalError);
            } else {
                this.originalError = new Error('Unknown Error', { cause: originalError });
            }
        }
        // 継承のための修正（ES5互換対応）
        Object.setPrototypeOf(this, new.target.prototype);
    }

    /**
     * エラーメッセージとスタックトレースを含む詳細情報を取得
     */
    getDetailedMessage(): string {
        let message = `${this.name} [${this.errorType}]: ${this.message}`;

        if (this.originalError) {
            if (this.message !== this.originalError.message)
                message += `\nOriginal Error: ${this.originalError.message}`;
            if (this.originalError.cause) {
                if (typeof this.originalError.cause === 'object') {
                    try { message += `cause: ${JSON.stringify(this.originalError.cause)}`; } catch { /* ignore */ }
                } else {
                    message += `cause: ${String(this.originalError.cause)}`;
                }
            }
        }

        return message;
    }

    /**
     * 既存の ApiError から EWBCIError に変換
     */
    static fromApiError(apiError: any): EWBCIError {
        // 既存の ErrorType から EWBCIErrorType にマッピング
        let errorType: EWBCIErrorType;

        switch (apiError.errorType) {
            case 'TokenNotFound':
                errorType = EWBCIErrorType.Unauthorized;
                break;
            case 'ServerError':
                errorType = EWBCIErrorType.ServerError;
                break;
            case 'NetworkError':
                errorType = EWBCIErrorType.ServerConnectionError;
                break;
            case 'EwbciTimeout':
                errorType = EWBCIErrorType.EwbConnectionError;
                break;
            default:
                errorType = EWBCIErrorType.Unknown;
        }

        return new EWBCIError(apiError.message, errorType, apiError);
    }

    /**
     * error(unknown) から EWBCIError に変換
     */
    static fromError(error: unknown, errorType: EWBCIErrorType = EWBCIErrorType.Unknown): EWBCIError {
        if (error instanceof Error) return new EWBCIError(error.message, errorType, error);
        if (typeof error === 'string') return new EWBCIError(error, errorType);
        return new EWBCIError('unknown error', errorType, error);
    }
}

/**
 * エラーが EWBCIError かどうかを判定
 */
export function isEWBCIError(error: unknown): error is EWBCIError {
    return error instanceof EWBCIError;
}

/**
 * エラーが特定の種類の EWBCIError かどうかを判定
 */
export function isEWBCIErrorType(error: unknown, errorType: EWBCIErrorType): boolean {
    return isEWBCIError(error) && error.errorType === errorType;
}

/** Client参照のアサーション */
export function assertClient(clientRef: React.MutableRefObject<EWBCIClient | null>): void {
    if (!clientRef.current)
        throw new EWBCIError('EWBCI client not initialized', EWBCIErrorType.AssertError);
}